import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ENTQuestionSetsParser as QuestionSetsParser } from 'ent-capability-quoteandbind-questionsets-js/index';
import messages from './QuestionSetComponent.messages';
import QuestionSetComponent from './QuestionSetComponent';
import generateQuestionSetsPresentationMetadata from './generateQuestionSetsPresentationMetadata';
import { QuestionSetUtils } from '@xengage/gw-portals-util-js';

/**
 * @ignore
 * @memberof module:gw-portals-questionsets-ui
 *
 * @property {Object} value - value of the property being rendered
 * @property {string} path - the path where this component is attached
 * @property {function(newAnswers, pathToAnswers)} onValueChange - the
 *                                      function invoked when the value is changed
 * @property {function(isComponentValid, id)} onValidate - the
 *                                      function when validation is triggered
 * @property {string} labelPosition - the position for the question label
 * @property {string} id - the id for the component
 * @property {Object} title - the translation ready title for the question sets component
 *
 * @returns {ReactElement}
 */
function QuestionSetContainer({
    id,
    onValidate,
    setIsQuestionSetValidation,
    onValueChange,
    value: aQuestionSetAnswersValue,
    path: pathToAnswers,
    labelPosition,
    title,
    showErrors,
    saveAndRender,
    //DE15433
    selectedPolicyType,
    selectedUWCompany,
    isStructureTypeDisabled
}) {
    const translator = useTranslator();
    const { isComponentValid, onValidate: onValidationChange, registerComponentValidation } = useValidation(id);
    const [questionSetsMetadata, setQuestionSetsMetadata] = useState(null);

    useEffect(() => {
        import(
            /* webpackChunkName: 'question-sets-metadata' */
            // eslint-disable-next-line import/no-unresolved
            'question-sets-metadata'
        ).then((data) => {
            const { default: result } = data;
            setQuestionSetsMetadata(result);
        });
    }, []);

    const checkComponentValidation = useCallback(() => {
        return questionSetsMetadata ? true : null;
    }, [questionSetsMetadata]);

    useEffect(() => {
        registerComponentValidation(checkComponentValidation);
    }, [checkComponentValidation, registerComponentValidation]);

    useEffect(() => {
        if (!setIsQuestionSetValidation && onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid, setIsQuestionSetValidation]);

    if (setIsQuestionSetValidation) {
        setIsQuestionSetValidation(isComponentValid);
    }

    const handleValueChange = useCallback(
        (value, path) => {
            if (value === undefined) {
                return;
            }

            const { code: questionSetCode, answers: questionSetAnwer } = aQuestionSetAnswersValue;
            const qsOrderedQuestions = questionSetsMetadata[questionSetCode].orderedQuestions;

            QuestionSetUtils.cleanDependantQuestions(
                qsOrderedQuestions,
                value,
                path,
                questionSetAnwer
            );


            let val = value;

            if (value.code) {
                val = value.code;
            }
            if (val.length === 0) {
                val = null;
            }

            

            _.set(aQuestionSetAnswersValue, `answers.${path}`, val);

            if (onValueChange) {
                onValueChange(aQuestionSetAnswersValue, pathToAnswers, path);
            }
        },
        [aQuestionSetAnswersValue, onValueChange, pathToAnswers, questionSetsMetadata]
    );

    if (!questionSetsMetadata) {
        return null;
    }

    const { viewModel: questionSetsViewModel } = new QuestionSetsParser(
        aQuestionSetAnswersValue,
        questionSetsMetadata,
        translator
    );
    //DE15433
    const generatedQuestionSetsMetadata = generateQuestionSetsPresentationMetadata(questionSetsViewModel, selectedPolicyType, selectedUWCompany);

    return (
        <QuestionSetComponent
            id={id}
            title={title}
            questionSetsMetadata={generatedQuestionSetsMetadata}
            questionSetsViewModel={questionSetsViewModel}
            onValueChange={handleValueChange}
            onValidationChange={onValidationChange}
            labelPosition={labelPosition}
            showErrors={showErrors}
            saveAndRender={saveAndRender}
            isStructureTypeDisabled={isStructureTypeDisabled}
        />
    );
}

QuestionSetContainer.propTypes = {
    value: PropTypes.shape({
        code: PropTypes.string,
    }).isRequired,
    path: PropTypes.string.isRequired,
    onValueChange: PropTypes.func,
    onValidate: PropTypes.func,
    labelPosition: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string,
    }),
    showErrors: PropTypes.bool
};
QuestionSetContainer.defaultProps = {
    labelPosition: 'left',
    title: messages.pageTitle,
};

export default QuestionSetContainer;
